import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const SettingsContext = createContext(null);
const CACHE_KEY = "eha_settings";

/**
 * Site settings (logo, hero photo, contact details) come from the server. The last copy is kept in the browser, so on
 * a repeat visit the page opens straight away with the current logo and photo instead of briefly showing the
 * built-in ones. On a first visit `settings` stays null until the server answers, and the header and hero show a
 * neutral placeholder rather than a picture that would then change. index.html starts the request before the
 * site's code has even downloaded (window.__EHA_SETTINGS).
 */
const readCache = () => {
    try {
        const v = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
        return v && typeof v === "object" ? v : null;
    } catch {
        return null;
    }
};
const writeCache = (v) => {
    try { if (v) localStorage.setItem(CACHE_KEY, JSON.stringify(v)); } catch { /* private mode */ }
};

export const SettingsProvider = ({ children }) => {
    const [settings, setSettingsState] = useState(readCache);

    const setSettings = (next) => {
        setSettingsState((prev) => {
            const v = typeof next === "function" ? next(prev) : next;
            writeCache(v);
            return v;
        });
    };

    const load = async (early) => {
        try {
            const data = early ? await early : null;
            if (data && typeof data === "object") return setSettings(data);
            const r = await api.get("/settings");
            setSettings(r.data);
        } catch (e) {
            console.error("Failed to load settings", e);
        }
    };

    useEffect(() => {
        const early = window.__EHA_SETTINGS;
        window.__EHA_SETTINGS = null;   // use the head start once; later reloads ask the server again
        load(early);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <SettingsContext.Provider value={{ settings, reload: () => load(), setSettings }}>
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettings = () => {
    const ctx = useContext(SettingsContext);
    return ctx || { settings: null };
};
